/**
 * The portal's light/dark choice (account#87).
 *
 * ── THREE STATES, NOT TWO ────────────────────────────────────────────
 *
 * `system` is not "light". It means *follow the OS*, and it is the default, so
 * a portal opened on a machine set to dark is dark without anyone choosing
 * anything. The other two are an explicit override that must win over the OS in
 * BOTH directions — including Light on a machine set to dark, which is the one
 * case a bare `prefers-color-scheme` media query cannot express. A two-state
 * toggle was settled against for the admin portal for exactly that reason, and
 * nothing here is different.
 *
 * ── THE CSS DOES THE WORK, NOT THIS FILE ─────────────────────────────
 *
 * `@ridgewill/brand`'s `tokens.css` ships the dark palette twice:
 *
 *     @media (prefers-color-scheme: dark) { :root:not([data-theme="light"]) { … } }
 *     :root[data-theme="dark"] { … }
 *
 * So `system` needs NO attribute at all — the media query handles it — and the
 * only job here is to stamp `data-theme` for an explicit choice. That is why
 * `applyTheme` REMOVES the attribute for `system` rather than setting it to
 * something: an attribute of `"system"` matches neither rule and would silently
 * pin the portal to light, which is the failure that looks most like success.
 *
 * ── tokens.css IS LOAD-BEARING NOW, WHICH IT WAS NOT BEFORE ──────────
 *
 * Every `C.*` token is `var(--rw-x, <light>)`, so this portal rendered
 * correctly for months while the stylesheet was merely nice to have — the
 * fallbacks carried it. Dark cannot work that way, because a fallback holds one
 * value and a theme needs two. `main.tsx` imports it; removing that import
 * would not break the portal, it would quietly delete dark mode and leave this
 * control doing nothing at all.
 *
 * This mirrors org-admin's `src/lib/theme.ts` deliberately. Two copies of forty
 * lines is the smaller cost: the alternative is publishing a shared React
 * module from `@ridgewill/brand`, which would put a framework dependency into a
 * package that is currently just tokens and CSS, for the sake of one component
 * each surface styles differently anyway.
 */

export type ThemeChoice = 'system' | 'light' | 'dark';

/**
 * Also read by the inline script in `index.html`. Changing it needs BOTH.
 *
 * Distinct from org-admin's `rw-admin-theme` on purpose: the two portals are
 * separate origins with separate storage, and a shared key would imply a shared
 * preference that cannot exist.
 */
export const THEME_KEY = 'rw-account-theme';

export const THEME_CHOICES: readonly ThemeChoice[] = ['system', 'light', 'dark'];

/** Human labels, so the control and its tests cannot drift apart. */
export const THEME_LABELS: Record<ThemeChoice, string> = {
  system: 'System',
  light: 'Light',
  dark: 'Dark',
};

function isChoice(v: unknown): v is ThemeChoice {
  return v === 'system' || v === 'light' || v === 'dark';
}

/**
 * `window.localStorage`, NOT the bare global — and this is not style.
 *
 * Node 22+ ships its own experimental `globalThis.localStorage`, which warns
 * "localStorage is not available because --localstorage-file was not provided"
 * and evaluates to `undefined`. Under jsdom that global is not replaced, so a
 * bare `localStorage` reference in a test run resolves to Node's undefined one
 * rather than jsdom's working implementation, and every call here fails with
 * `Cannot read properties of undefined`.
 *
 * In a real browser the two spellings are identical, which is what makes this
 * worth a comment: the bare form is not wrong in production, it is wrong only
 * where the tests run, so the module would have shipped untestable and the
 * failure would have read as a broken harness.
 */
function store(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/**
 * What was last chosen, or `system`.
 *
 * Every access is guarded. `localStorage` is not merely empty in a private
 * window or with site data blocked — reading it THROWS, and an exception here
 * would take the whole portal down over a colour preference. The portal has
 * already paid once for a failure that presented as a blank page (see
 * `sessionState` in App.tsx); this is the same class and costs one try/catch.
 */
export function readTheme(): ThemeChoice {
  try {
    const raw = store()?.getItem(THEME_KEY);
    return isChoice(raw) ? raw : 'system';
  } catch {
    return 'system';
  }
}

/** Stamp the root so the `[data-theme]` rules in tokens.css apply. */
export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);
}

/** Remember the choice and apply it. Storage failing must not stop the change. */
export function setTheme(choice: ThemeChoice): void {
  try {
    if (choice === 'system') store()?.removeItem(THEME_KEY);
    else store()?.setItem(THEME_KEY, choice);
  } catch {
    // A private window can still switch theme for this tab; it just will not be
    // remembered. Losing the preference is a smaller failure than refusing to
    // honour it.
  }
  applyTheme(choice);
}

/** What the viewer actually sees right now, which `system` alone does not say. */
export function resolvedTheme(choice: ThemeChoice): 'light' | 'dark' {
  if (choice !== 'system') return choice;
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}
